import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Clock, DollarSign, Pencil } from "lucide-react";

const EMPTY = { name: "", description: "", price: 50, duration_min: 60, active: true };

export default function Services() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/services");
    setServices(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm(s); setEditing(s.id); setOpen(true); };

  const submit = async () => {
    try {
      if (editing) await api.put(`/services/${editing}`, form);
      else await api.post("/services", form);
      toast.success(editing ? "Service updated" : "Service added");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-accent">Catalog</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Services & pricing</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile" data-testid="btn-new-service">
              <Plus className="w-4 h-4 mr-1"/> New service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} service</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-name"/></div>
              <div><Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="svc-desc"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price ($)</Label>
                  <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: +e.target.value })} data-testid="svc-price"/></div>
                <div><Label>Duration (min)</Label>
                  <Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: +e.target.value })} data-testid="svc-duration"/></div>
              </div>
              <Button onClick={submit} className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="btn-save-service">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map(s => (
          <div key={s.id} className="bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm card-tactile relative"
            data-testid={`service-${s.id}`}>
            <button onClick={() => openEdit(s)} className="absolute top-4 right-4 text-[#6B625B] hover:text-[#C85A17]"
              data-testid={`edit-${s.id}`}>
              <Pencil className="w-4 h-4"/>
            </button>
            <h3 className="font-display text-xl font-bold pr-8">{s.name}</h3>
            <p className="text-sm text-[#6B625B] mt-2 min-h-[3rem]">{s.description}</p>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-[#C85A17] font-semibold"><DollarSign className="w-4 h-4"/>{s.price}</span>
              <span className="flex items-center gap-1 text-[#6B625B]"><Clock className="w-4 h-4"/>{s.duration_min} min</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
